import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { I18n } from '@lingui/core';

import { Input } from '$/components/ui/Input/Basics/Input';
import { Portal } from '$/components/Portal';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';

import { useGuessCountryQuery } from '$/GraphQL/schema/schema';
import {
  EwebinarSessions,
  OptionFieldInput,
  RegistrationFieldType,
  RegistrationFormField,
  SelectionType,
} from '$/GraphQL/schema/types';

import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useMe } from '$/hooks/useMe';

import { markdownToText } from '$/shared/frontend/utils/MarkdownConverter';

import { getAttendeeFieldValueFieldName } from '$/utils/attendeeFormField';
import isMobile from '$/utils/isMobile';

import { usePageEditorContext } from '../PageEditorContext';
import { EditingField, FieldTypeOption, RenderFieldProps } from './RegistrationForm.types';
import { RegFormSettingsForForm } from './types';
import { useFieldTypeOptions } from './useFieldTypeOptions';
import { useRenderFields } from './renderField';
import { AddFieldDropdown } from './AddFieldDropdown';
import { UseFullNameFieldDropdown } from './UseFullNameFieldDropdown';
import { ConsentCheckboxField } from './ConsentCheckboxField';
import { SessionField } from './SessionField';
import DropdownEditModal from './DropdownEditModal';

export const useRegistrationFormFields = ({
  requireSSO,
  excludeProperties: excludePropertiesProps,
  i18n,
  isInVideoRegistration,
  showConditionsPopup,
  hideConsentCheckbox,
  sessions,
}: {
  requireSSO?: boolean;
  excludeProperties?: string[];
  i18n: I18n;
  isInVideoRegistration?: boolean;
  showConditionsPopup?: boolean;
  hideConsentCheckbox?: boolean;
  sessions?: EwebinarSessions;
}) => {
  const me = useMe();
  const state = usePageEditorContext();
  const countryQuery = useGuessCountryQuery();
  const { watch, control, setValue, clearErrors } = useFormContext<
    RegFormSettingsForForm & { [k: string]: any }
  >();
  const { fields, remove, move, append } = useFieldArray({
    control,
    name: 'fields',
  });

  const [country, setCountry] = React.useState(
    countryQuery?.data?.guessCountry ?? me?.lastCountry ?? 'us'
  );
  const [fieldEditing, setFieldEditing] = React.useState<EditingField>();
  const [draggingIndex, setDraggingIndex] = React.useState<number>();

  const inputRefFirstName = React.useCallback((node: HTMLInputElement | null) => {
    !isMobile && node?.focus();
  }, []);

  const { mode } = state;
  const fieldTypeOptions = useFieldTypeOptions(country, i18n);
  const lastNameFieldTypeOptions: FieldTypeOption[] = React.useMemo(() => {
    return [
      {
        value: RegistrationFieldType.Text,
        text: RegistrationFieldType.Text,
        component: <Input />,
      },
      {
        value: RegistrationFieldType.Hidden,
        text: RegistrationFieldType.Hidden,
        component: <Input />,
      },
    ];
  }, []);

  const useFullNameField = watch('useFullNameField');
  const fieldValues: RegistrationFormField[] | undefined = watch('fields');
  const regFields = React.useMemo(() => fields.filter(Boolean), [fields]);
  const fieldChanged = useHasChangedShallow([JSON.stringify(fieldValues)]);
  let firstHiddenFieldIndex =
    fieldValues?.findIndex(({ type }, index) => {
      const isLastNameField = index === 1;
      return !isLastNameField && type === RegistrationFieldType.Hidden;
    }) ?? -1;
  if (firstHiddenFieldIndex === -1) {
    firstHiddenFieldIndex = regFields.length;
  }

  const currentPropertiesInForm = React.useMemo(() => {
    return regFields.filter((p) => p.propertyName).map((f) => f.propertyName!.toLowerCase());
  }, [regFields]);
  const excludeProperties = React.useMemo(() => {
    return currentPropertiesInForm.concat(excludePropertiesProps ?? []);
  }, [currentPropertiesInForm, excludePropertiesProps]);

  const draggableFrom = 3;
  const onMove = React.useCallback(
    ({
      removedIndex,
      addedIndex,
    }: {
      removedIndex: number | null;
      addedIndex: number | null;
    }): void => {
      if (removedIndex !== null && addedIndex !== null) {
        move(removedIndex + draggableFrom, addedIndex + draggableFrom);
      }
    },
    [move]
  );

  const onUpdatingOptions = React.useCallback(
    (index: number, subType: SelectionType, options: OptionFieldInput[]): void => {
      setValue(`fields.${index}.subType`, subType, { shouldDirty: true });
      setValue(`fields.${index}.options`, options, { shouldDirty: true });
      clearErrors(`fields.${index}.propertyName`);
    },
    [clearErrors, setValue]
  );

  // Move all hidden fields to the end of the form
  React.useEffect(() => {
    if (
      !fieldChanged ||
      mode !== PageMode.edit ||
      !fieldValues ||
      !fieldValues.filter(Boolean).length
    )
      return;

    const shouldMoveIndex = fieldValues.findIndex((field, index) => {
      const isLastNameField = index === 1;
      return (
        field.type === RegistrationFieldType.Hidden &&
        !isLastNameField &&
        fieldValues[index + 1]?.type &&
        fieldValues[index + 1].type !== RegistrationFieldType.Hidden
      );
    });
    if (shouldMoveIndex !== -1) {
      move(shouldMoveIndex, fieldValues.length - 1);
    }
  }, [move, fieldChanged, fieldValues, mode]);

  // Auto set country into country field
  const guessedCountry = countryQuery?.data?.guessCountry;
  const useFieldsHaveChanged = useHasChangedShallow([guessedCountry, fields]);
  const hasSetCountry = React.useRef(false);
  React.useEffect(() => {
    if (!useFieldsHaveChanged || !guessedCountry || hasSetCountry.current) return;

    setCountry(guessedCountry);

    fields.forEach((field, index): void => {
      if (field.type === RegistrationFieldType.Countries) {
        const fieldName = getAttendeeFieldValueFieldName(index) as keyof RegFormSettingsForForm;
        setValue(fieldName, guessedCountry!.toLocaleUpperCase(), { shouldDirty: true });
      }
    });
    hasSetCountry.current = true;
  }, [guessedCountry, fields, setValue, useFieldsHaveChanged, useFullNameField]);

  const builtInFields = React.useMemo(
    () => [
      {
        id: 'name',
        fieldIndex: -1,
        canChangeType: false,
        isLastNameField: false,
        showPropertyInput: true,
        disabled: requireSSO,
        hidden: !useFullNameField,
        fieldName: 'fullNameField',
        attendeeValueFieldName: 'name',
        attendeeFieldName: 'fullNameField',
        excludeProperties,
      },
      ...regFields.slice(0, 3).map(({ id }, fieldIndex): RenderFieldProps => {
        const propertyName = getAttendeeFieldValueFieldName(fieldIndex);
        const isLastNameField = propertyName === 'lastName';
        return {
          id,
          fieldIndex,
          isLastNameField,
          disabled: requireSSO,
          showPropertyInput: true,
          canChangeType: false,
          hidden: useFullNameField && propertyName !== 'email',
          typeOptions: isLastNameField ? lastNameFieldTypeOptions : fieldTypeOptions,
          attendeeValueFieldName: propertyName,
          inputRef: fieldIndex === 0 ? inputRefFirstName : undefined,
          excludeProperties,
          placeholder:
            propertyName === 'email' && state.series
              ? 'Email address is automatically populated from Step 1'
              : undefined,
          hideNote: Boolean(propertyName === 'email' && state.series),
        };
      }),
    ],
    [
      excludeProperties,
      fieldTypeOptions,
      inputRefFirstName,
      lastNameFieldTypeOptions,
      regFields,
      requireSSO,
      useFullNameField,
      state.series,
    ]
  );

  const draggableFields = React.useMemo(
    () =>
      regFields.slice(3, firstHiddenFieldIndex).map((formField, index): RenderFieldProps => {
        const fieldIndex = 3 + index;
        return {
          id: formField.id,
          formField,
          fieldIndex,
          isLastNameField: false,
          showPropertyInput: false,
          draggable: mode === PageMode.edit && regFields.slice(3, firstHiddenFieldIndex).length > 1,
          canChangeType: mode === PageMode.edit,
          excludeProperties,
          onRemove: (): void => remove(fieldIndex),
        };
      }),
    [excludeProperties, firstHiddenFieldIndex, mode, regFields, remove]
  );

  const hiddenFields = React.useMemo(
    () =>
      regFields.slice(firstHiddenFieldIndex).map((formField, index) => {
        const fieldIndex = firstHiddenFieldIndex + index;
        return {
          id: formField.id,
          formField,
          fieldIndex,
          isLastNameField: false,
          showPropertyInput: false,
          canChangeType: mode === PageMode.edit,
          excludeProperties,
          onRemove: (): void => remove(fieldIndex),
        };
      }),
    [excludeProperties, firstHiddenFieldIndex, mode, regFields, remove]
  );

  const renderFields = useRenderFields({
    country,
    i18n,
    setCountry,
    draggingIndex,
    isInVideoRegistration,
    setFieldEditing,
  });

  const fieldsElements = React.useMemo(() => {
    return (
      <>
        {!isInVideoRegistration && <SessionField sessions={sessions} />}
        <UseFullNameFieldDropdown />
        {renderFields({
          builtInFields,
          draggableFields,
          hiddenFields,
          onMove,
          setDraggingIndex,
        })}
        {mode === PageMode.edit && <AddFieldDropdown country={country} append={append} />}
        <ConsentCheckboxField
          i18n={i18n}
          isInVideoRegistration={isInVideoRegistration}
          hideConsentCheckbox={hideConsentCheckbox}
          showConditionsPopup={showConditionsPopup}
          tabIndex={regFields.length + 1}
        />

        {mode === PageMode.edit && fieldEditing && (
          <Portal containerId='optionFieldsModal'>
            <React.Suspense>
              <DropdownEditModal
                title={markdownToText(fieldEditing.fieldName)}
                fieldIndex={fieldEditing.fieldIndex}
                subType={fieldEditing.subType}
                options={fieldEditing.options}
                onSave={onUpdatingOptions}
                onClose={(): void => setFieldEditing(undefined)}
              />
            </React.Suspense>
          </Portal>
        )}
      </>
    );
  }, [
    sessions,
    append,
    builtInFields,
    country,
    draggableFields,
    hiddenFields,
    hideConsentCheckbox,
    i18n,
    isInVideoRegistration,
    mode,
    onMove,
    regFields.length,
    renderFields,
    showConditionsPopup,
    fieldEditing,
    onUpdatingOptions,
  ]);

  return {
    fieldsElements,
    fieldEditing,
  };
};
